import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import StoryCard from '@/components/base/StoryCard';
import { SITE_NAME, SITE_ORIGIN, useSEO } from '@/hooks/useSEO';
import { getRelatedStories, getStoryById } from '@/lib/content';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const story = id ? getStoryById(id) : undefined;

  const related = useMemo(() => (story ? getRelatedStories(story, 3) : []), [story]);

  const jsonLd = useMemo(() => {
    if (!story) return undefined;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: story.title,
          description: story.summary,
          image: story.cover,
          datePublished: story.date,
          dateModified: story.date,
          inLanguage: 'zh-CN',
          articleSection: story.categoryName,
          keywords: story.tags.join(','),
          author: { '@type': 'Organization', name: story.author },
          publisher: { '@type': 'Organization', name: SITE_NAME, url: `${SITE_ORIGIN}/` },
          mainEntityOfPage: `${SITE_ORIGIN}/article/${story.id}`,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_ORIGIN}/` },
            { '@type': 'ListItem', position: 2, name: '热门速览', item: `${SITE_ORIGIN}/hot` },
            { '@type': 'ListItem', position: 3, name: story.title, item: `${SITE_ORIGIN}/article/${story.id}` },
          ],
        },
      ],
    };
  }, [story]);

  useSEO({
    title: story ? `${story.title} | 5看料网速览` : '速览详情 | 5看料网',
    description: story
      ? `${story.summary.slice(0, 110)}`
      : '5看料网速览详情页，提供速览要点、精简摘要与完整正文。',
    keywords: story ? `5看料网速览,${story.tags.join(',')},${story.categoryName}` : '5看料网速览',
    path: `/article/${id ?? ''}`,
    ogType: 'article',
    image: story?.cover,
    jsonLd,
  });

  if (!story) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto max-w-2xl rounded-lg border border-background-300 bg-background-100 p-10 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">这条速览找不到了</h1>
          <p className="mt-2 font-body text-sm text-foreground-600">
            它可能已经被合并或调整了位置，去列表页看看最新的速览吧。
          </p>
          <Link
            to="/hot"
            className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回速览列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100 py-6 md:py-8">
        <div className="w-full px-4 md:px-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 font-body text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <Link to="/hot" className="transition-colors hover:text-primary-600">
              热门速览
            </Link>
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
            <Link to={`/hot?category=${story.category}`} className="transition-colors hover:text-primary-600">
              {story.categoryName}
            </Link>
          </nav>
        </div>
      </div>

      <div className="w-full px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            <Link
              to={`/hot?category=${story.category}`}
              className="inline-flex items-center gap-2 rounded-md bg-primary-100 px-3 py-1.5 font-label text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
            >
              <i className="ri-price-tag-3-line text-sm" aria-hidden="true"></i>
              {story.categoryName}
            </Link>

            <h1 className="mt-4 font-heading text-2xl font-black leading-[1.35] text-foreground-950 md:text-4xl">
              {story.title}
            </h1>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 font-label text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line text-sm" aria-hidden="true"></i>
                {story.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
                {story.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm" aria-hidden="true"></i>
                阅读约 {story.readTime}
              </span>
              <span className="flex items-center gap-1.5 text-accent-700">
                <i className="ri-fire-line text-sm" aria-hidden="true"></i>
                热度 {story.heat}
              </span>
            </div>

            <div className="mt-7 h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[400px]">
              <img
                src={story.cover}
                alt={story.coverAlt}
                title={`${story.title} - 5看料网速览`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <section className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-800">
                <i className="ri-flashlight-line text-lg" aria-hidden="true"></i>
                速览要点
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {story.points.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-base text-primary-500" aria-hidden="true"></i>
                    <span className="font-body text-sm leading-relaxed text-foreground-800">{point}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                <i className="ri-file-text-line text-lg" aria-hidden="true"></i>
                精简摘要
              </h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-800">{story.summary}</p>
            </section>

            <section className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950">完整整理</h2>
              <div className="mt-4 flex flex-col gap-4">
                {story.body.map((paragraph) => (
                  <p key={paragraph.slice(0, 18)} className="font-body text-sm leading-[1.9] text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="font-label text-xs text-foreground-500">相关标签</span>
              {story.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/hot?q=${encodeURIComponent(tag)}`}
                  className="rounded-md bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">速览信息</h2>
                <dl className="mt-4 flex flex-col gap-3 font-body text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">所属板块</dt>
                    <dd className="font-medium text-foreground-900">{story.categoryName}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">整理来源</dt>
                    <dd className="font-medium text-foreground-900">{story.source}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">更新时间</dt>
                    <dd className="font-medium text-foreground-900">{story.date}</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">阅读时长</dt>
                    <dd className="font-medium text-foreground-900">{story.readTime}</dd>
                  </div>
                </dl>
                <Link
                  to={`/hot?category=${story.category}`}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  查看更多{story.categoryName}
                  <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                </Link>
              </div>

              <div className="rounded-lg bg-primary-950 p-5">
                <h2 className="font-heading text-sm font-bold text-background-50">订阅速览汇总</h2>
                <p className="mt-2 font-body text-xs leading-relaxed text-background-300">
                  每期更新后，我们会把最值得看的速览要点整理成一封精简邮件。
                </p>
                <Link
                  to="/#subscribe"
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-mail-send-line text-base" aria-hidden="true"></i>
                  免费订阅
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {related.length > 0 && (
        <section className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-8">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关速览推荐</h2>
              <Link
                to="/hot"
                className="inline-flex items-center gap-1 whitespace-nowrap font-label text-sm text-primary-600 transition-colors hover:text-primary-700"
              >
                全部速览
                <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <StoryCard key={item.id} story={item} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}